import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/App';
import Navbar from '@/components/Navbar';

// Helper function to convert YouTube URLs to embed URLs
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  
  // Already an embed URL
  if (url.includes('youtube.com/embed/') || url.includes('youtu.be/embed/')) {
    return url;
  }
  
  // Extract video ID from various YouTube URL formats
  let videoId = null;
  
  // Format: https://www.youtube.com/watch?v=VIDEO_ID
  if (url.includes('youtube.com/watch?v=')) {
    videoId = url.split('watch?v=')[1]?.split('&')[0];
  }
  // Format: https://youtu.be/VIDEO_ID
  else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split('?')[0];
  }
  // Format: https://www.youtube.com/v/VIDEO_ID
  else if (url.includes('youtube.com/v/')) {
    videoId = url.split('youtube.com/v/')[1]?.split('?')[0];
  }
  
  // If we found a video ID, return the embed URL
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }
  
  // For non-YouTube videos (Vimeo, etc.), return as-is
  return url;
};

export default function AboutPage({ user, onLogout }) {
  const [clubInfo, setClubInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    photos: [],
    videos: []
  });

  useEffect(() => {
    loadClubInfo();
  }, []);

  const loadClubInfo = async () => {
    try {
      const response = await api.get('/club-info');
      setClubInfo(response.data);
      setFormData({
        title: response.data.title,
        content: response.data.content,
        photos: response.data.photos || [],
        videos: response.data.videos || []
      });
    } catch (error) {
      toast.error('Failed to load club information');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.put('/club-info', formData);
      toast.success('Club information updated!');
      setEditing(false);
      loadClubInfo();
    } catch (error) {
      toast.error('Failed to update club information');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center py-20">
          <div className="text-2xl font-semibold text-gray-700">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            {editing ? (
              <div className="flex-1">
                <Label htmlFor="title">Club Title</Label>
                <Input
                  id="title"
                  data-testid="club-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="mt-2 text-2xl font-bold"
                />
              </div>
            ) : (
              <h1 className="text-4xl font-bold text-gray-900">{clubInfo?.title}</h1>
            )}

            {user && user.is_admin && (
              <div className="flex gap-2">
                {editing ? (
                  <>
                    <Button
                      data-testid="save-club-info-btn"
                      onClick={handleSave}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Save
                    </Button>
                    <Button
                      data-testid="cancel-edit-btn"
                      onClick={() => {
                        setEditing(false);
                        setFormData({
                          title: clubInfo.title,
                          content: clubInfo.content,
                          photos: clubInfo.photos || [],
                          videos: clubInfo.videos || []
                        });
                      }}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    data-testid="edit-club-info-btn"
                    onClick={() => setEditing(true)}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    Edit
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="mb-8">
            {editing ? (
              <div>
                <Label htmlFor="content">Club Description</Label>
                <Textarea
                  id="content"
                  data-testid="club-content-textarea"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={10}
                  className="mt-2"
                />
              </div>
            ) : (
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 whitespace-pre-wrap">{clubInfo?.content}</p>
              </div>
            )}
          </div>

          {/* Photos and Videos Section */}
          {editing && (
            <div className="mb-8 space-y-6">
              {/* Photo URLs Input */}
              <div>
                <Label htmlFor="photo-url">Add Photo URL</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    id="photo-url"
                    placeholder="https://example.com/photo.jpg"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const url = e.target.value.trim();
                        if (url) {
                          setFormData({
                            ...formData,
                            photos: [...(formData.photos || []), url]
                          });
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={(e) => {
                      const input = document.getElementById('photo-url');
                      const url = input.value.trim();
                      if (url) {
                        setFormData({
                          ...formData,
                          photos: [...(formData.photos || []), url]
                        });
                        input.value = '';
                      }
                    }}
                  >
                    Add Photo
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-1">Press Enter or click Add Photo to add</p>
                
                {/* Display added photos */}
                {formData.photos && formData.photos.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                    {formData.photos.map((photo, index) => (
                      <div key={index} className="relative">
                        <img
                          src={photo}
                          alt={`Photo ${index + 1}`}
                          className="rounded-lg object-cover w-full h-32"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute top-1 right-1"
                          onClick={() => {
                            const newPhotos = formData.photos.filter((_, i) => i !== index);
                            setFormData({...formData, photos: newPhotos});
                          }}
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Video URLs Input */}
              <div>
                <Label htmlFor="video-url">Add Video URL (YouTube, Vimeo, etc.)</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    id="video-url"
                    placeholder="https://youtube.com/watch?v=..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const url = e.target.value.trim();
                        if (url) {
                          setFormData({
                            ...formData,
                            videos: [...(formData.videos || []), url]
                          });
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={(e) => {
                      const input = document.getElementById('video-url');
                      const url = input.value.trim();
                      if (url) {
                        setFormData({
                          ...formData,
                          videos: [...(formData.videos || []), url]
                        });
                        input.value = '';
                      }
                    }}
                  >
                    Add Video
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-1">Press Enter or click Add Video to add</p>
                
                {/* Display added videos */}
                {formData.videos && formData.videos.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {formData.videos.map((video, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 bg-gray-100 rounded">
                        <span className="flex-1 text-sm truncate">{video}</span>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            const newVideos = formData.videos.filter((_, i) => i !== index);
                            setFormData({...formData, videos: newVideos});
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Photos Section - View Mode */}
          {!editing && clubInfo?.photos && clubInfo.photos.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Gallery</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {clubInfo.photos.map((photo, index) => (
                  <img
                    key={index}
                    src={photo}
                    alt={`Club ${index + 1}`}
                    className="rounded-lg object-cover w-full h-48 hover-lift"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Videos Section - View Mode */}
          {!editing && clubInfo?.videos && clubInfo.videos.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Videos</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {clubInfo.videos.map((video, index) => {
                  const embedUrl = getYouTubeEmbedUrl(video);
                  return embedUrl ? (
                    <div key={index} className="aspect-video">
                      <iframe
                        src={embedUrl}
                        title={`Video ${index + 1}`}
                        className="w-full h-full rounded-lg shadow-md"
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      />
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
